import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { TypewriterText } from '../hooks/useTypewriter';
import {
  Panel, LossChart, ResidualBars, SurpriseText, ListeningBars, StatusChip, listeningCentre,
  formatBytes, formatCount, ago, LIVE_STATUSES, LIVE, SIGNAL, DANGER,
} from '../components/RunCharts';
import RunSettings from '../components/RunSettings';

const API = '/api';
const TRAIL_KINDS = 'status,control,revision,uploaded,validation,log,warning,error,benchmark,resumed,billing';

function headers() {
  const h = { 'Content-Type': 'application/json' };
  const t = localStorage.getItem('logos_session_token');
  if (t) h['Authorization'] = `Bearer ${t}`;
  return h;
}

function Stat({ label, value, tone }) {
  return (
    <div className="min-w-0">
      <div className="font-mono text-[10px] text-[#555555] lowercase">{label}</div>
      <div className="font-mono text-sm truncate" style={{ color: tone || '#dddddd' }}>{value}</div>
    </div>
  );
}

function trailLine(e) {
  const p = e.payload || {};
  const time = new Date(e.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  switch (e.kind) {
    case 'status': return [time, `${p.status}${p.note ? ` · ${p.note}` : ''}`, p.status === 'failed' || p.status === 'lost' ? DANGER : '#aaaaaa'];
    case 'control':
      if (p.pause) return [time, `pause requested${p.by ? ` by ${p.by}` : ''}`, SIGNAL];
      if (p.resume) return [time, `resumed${p.settings && Object.keys(p.settings).length ? ` with ${Object.entries(p.settings).map(([k, v]) => `${k} ${v}`).join(', ')}` : ''}${p.by ? ` by ${p.by}` : ''}`, LIVE];
      return [time, `stop and save requested${p.by ? ` by ${p.by}` : ''}${p.reason ? ` (${p.reason})` : ''}`, SIGNAL];
    case 'revision': return [time, `revision ${String(p.revision_id || '').slice(0, 10)} is the head · ${formatBytes(p.bytes_seen)} seen`, LIVE];
    case 'uploaded': return [time, `uploaded ${formatBytes(p.bytes)} in ${p.seconds}s`, '#888888'];
    case 'validation': return [time, `held-out ${Number(p.bpb).toFixed(3)} bits per byte`, LIVE];
    case 'benchmark': return [time, p.bps ? `width ${p.width}: ${formatBytes(p.bps)}/s` : `width ${p.width}: ${p.error || 'failed'}`, '#aaaaaa'];
    case 'resumed': return [time, `moved to a ${p.gpu} (${p.reason}); continuing from the last save`, SIGNAL];
    case 'billing': return [time, `charged $${((p.charged_cents || 0) / 100).toFixed(2)}`, '#aaaaaa'];
    case 'error': return [time, p.text || JSON.stringify(p), DANGER];
    case 'warning': return [time, p.text || JSON.stringify(p), SIGNAL];
    default: return [time, p.text || JSON.stringify(p), '#777777'];
  }
}

export default function Run() {
  const { runId } = useParams();
  const { user } = useAuth();
  const [run, setRun] = useState(null);
  const [missing, setMissing] = useState(false);
  const [points, setPoints] = useState([]);
  const [trail, setTrail] = useState([]);
  const [dream, setDream] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [changes, setChanges] = useState({});
  const seq = useRef({ points: 0, trail: 0 });

  const load = useCallback(async () => {
    const res = await fetch(`${API}/runs/${runId}`, { headers: headers(), credentials: 'include' });
    if (res.status === 404) { setMissing(true); return; }
    if (!res.ok) return;
    setRun(await res.json());
    const [p, t, d] = await Promise.all([
      fetch(`${API}/runs/${runId}/events?kinds=point&after=${seq.current.points}`, { headers: headers(), credentials: 'include' }),
      fetch(`${API}/runs/${runId}/events?kinds=${TRAIL_KINDS}&after=${seq.current.trail}`, { headers: headers(), credentials: 'include' }),
      fetch(`${API}/runs/${runId}/events?kinds=dream&tail=1`, { headers: headers(), credentials: 'include' }),
    ]);
    if (p.ok) {
      const fresh = await p.json();
      if (fresh.length) {
        seq.current.points = fresh[fresh.length - 1].seq;
        setPoints(old => [...old, ...fresh.map(e => e.payload)]);
      }
    }
    if (t.ok) {
      const fresh = await t.json();
      if (fresh.length) {
        seq.current.trail = fresh[fresh.length - 1].seq;
        setTrail(old => [...old, ...fresh].slice(-400));
      }
    }
    if (d.ok) {
      const last = (await d.json())[0];
      if (last) setDream(last.payload);
    }
  }, [runId]);

  useEffect(() => {
    seq.current = { points: 0, trail: 0 };
    setPoints([]); setTrail([]);
    load();
  }, [load]);

  const live = run && LIVE_STATUSES.includes(run.status);
  useEffect(() => {
    if (!live) return undefined;
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [live, load]);

  const stop = async (save) => {
    const question = save
      ? 'stop and save? the machine saves, uploads the model and shuts down.'
      : 'stop now? the machine is destroyed at once; anything since the last uploaded save is lost.';
    if (!window.confirm(question)) return;
    setBusy(true); setMessage(null);
    const res = await fetch(`${API}/runs/${runId}/stop`, {
      method: 'POST', headers: headers(), credentials: 'include', body: JSON.stringify({ save }),
    });
    const body = await res.json().catch(() => ({}));
    setBusy(false);
    if (!res.ok) { setMessage(body.detail || 'that did not work'); return; }
    setRun(r => ({ ...r, ...body }));
    setMessage(save ? 'asked the machine to save and stop' : 'the machine is being destroyed');
    load();
  };

  const control = async (action, body = {}) => {
    setBusy(true); setMessage(null);
    const res = await fetch(`${API}/runs/${runId}/${action}`, {
      method: 'POST', headers: headers(), credentials: 'include', body: JSON.stringify(body),
    });
    const answer = await res.json().catch(() => ({}));
    setBusy(false);
    if (!res.ok) { setMessage(answer.detail || 'that did not work'); return; }
    setRun(r => ({ ...r, ...answer }));
    setMessage(action === 'pause' ? 'asked the machine to save and pause; it stays rented while paused'
      : 'resuming' + (Object.keys(changes).length ? ` with ${Object.entries(changes).map(([k, v]) => `${k} ${v}`).join(', ')}` : ''));
    setChanges({});
    load();
  };

  if (missing) return (
    <div className="min-h-screen bg-black flex items-center justify-center px-6">
      <p className="text-[#888888] lowercase">{user ? 'no such run, or it is not yours to see' : 'sign in to see this run'}</p>
    </div>
  );
  if (!run) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <p className="text-[#888888] lowercase animate-pulse">loading run...</p>
    </div>
  );

  const latest = run.latest || {};
  const report = latest.report || {};
  // io2's traversal band (runtimes before 1.8 called it metabolic_band)
  const band = report.traversal_band ?? report.metabolic_band;
  const machine = latest.machine || {};
  const bpbSeries = points.map(p => p.bpb).filter(v => typeof v === 'number');
  const validation = points.filter(p => typeof p.validation_bpb === 'number');
  const residualKey = report.residual_spectrum ? 'residual_spectrum' : 'residual_excess';
  const scored = (report.surprise || []).map(t => t[1]).filter(b => typeof b === 'number');
  const centre = listeningCentre(report.listening);
  const startCentre = listeningCentre(latest.listening_start);
  const progress = typeof latest.progress === 'number' ? latest.progress : null;
  const benchmark = latest.benchmark || [];

  return (
    <div className="min-h-screen bg-black py-10 px-4 sm:px-6 lg:px-8 page-wrapper" data-testid="run-page">
      <div className="max-w-6xl mx-auto space-y-8">
        <div>
          <Link to="/train" className="font-mono text-xs text-[#555555] hover:text-white lowercase">← train</Link>
          <div className="flex items-start justify-between gap-4 flex-wrap mt-3">
            <div className="min-w-0">
              <h1 className="text-3xl sm:text-4xl font-bold text-white lowercase font-mono break-words">
                <TypewriterText text={run.name} speed={40} />
              </h1>
              <div className="flex items-center gap-3 flex-wrap mt-2 font-mono text-xs text-[#888888] lowercase">
                <StatusChip status={run.status} />
                <span>{run.kind}</span>
                <span>{run.runtime_id} {run.runtime_version}</span>
                <span>{run.gpu_name}</span>
                {run.model && <Link to={`/checkpoints/${run.checkpoint_id}`} className="text-white hover:underline">{run.model.name} ↗</Link>}
              </div>
            </div>
            {run.can_control && live && (
              <div className="flex gap-2 flex-wrap">
                {run.kind === 'train' && ['preparing', 'training'].includes(run.status) && !(run.control || {}).pause && (
                  <button onClick={() => control('pause')} disabled={busy}
                    className="border border-[#383f38] text-white px-5 py-2 rounded-lg font-mono text-sm lowercase hover:border-white transition-all disabled:opacity-40"
                    data-testid="run-pause">pause</button>
                )}
                <button onClick={() => stop(true)} disabled={busy || (run.control || {}).stop === 'save'}
                  className="border border-white bg-white text-black px-5 py-2 rounded-lg font-mono text-sm lowercase hover:bg-transparent hover:text-white transition-all disabled:opacity-40"
                  data-testid="run-stop-save">
                  {(run.control || {}).stop === 'save' ? 'saving…' : 'stop and save'}
                </button>
                <button onClick={() => stop(false)} disabled={busy}
                  className="border px-5 py-2 rounded-lg font-mono text-sm lowercase transition-all disabled:opacity-40 hover:bg-[rgba(255,92,92,0.1)]"
                  style={{ borderColor: 'rgba(255,92,92,0.5)', color: DANGER }} data-testid="run-stop-now">
                  stop now
                </button>
              </div>
            )}
          </div>
          {message && <p className="mt-3 font-mono text-xs text-white lowercase">{message}</p>}
          {run.error && <p className="mt-3 font-mono text-xs lowercase whitespace-pre-wrap" style={{ color: DANGER }}>{run.error.split('\n')[0]}</p>}
        </div>

        {run.can_control && (run.control || {}).pause && live && (
          <div className="border rounded-xl p-4 sm:p-5 space-y-4" style={{ borderColor: 'rgba(255,158,66,0.45)' }}>
            <p className="font-mono text-sm lowercase" style={{ color: SIGNAL }}>
              {run.status === 'paused'
                ? `paused · the model is saved and the machine is still rented ($${(run.dollars_per_hour || 0).toFixed(3)}/h). a pause ends the run after two hours.`
                : 'pausing · saving and uploading first'}
            </p>
            {run.status === 'paused' && (
              <>
                <RunSettings fields={run.settings_fields || []} current={(run.plan || {}).shape || {}} lockShape startOpen onChange={setChanges} />
                <button onClick={() => control('resume', { settings: changes })} disabled={busy}
                  className="border border-white bg-white text-black px-5 py-2 rounded-lg font-mono text-sm lowercase hover:bg-transparent hover:text-white transition-all disabled:opacity-40"
                  data-testid="run-resume">
                  resume{Object.keys(changes).length ? ` with ${Object.keys(changes).length} change${Object.keys(changes).length === 1 ? '' : 's'}` : ''}
                </button>
              </>
            )}
          </div>
        )}

        {/* headline and stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 border-y border-[#1e251f] py-4">
          <div className="col-span-2 sm:col-span-1">
            <div className="font-mono text-[10px] text-[#555555] lowercase">bits per byte</div>
            <div className="font-mono text-3xl text-white">{typeof report.bpb === 'number' ? report.bpb.toFixed(3) : '–'}</div>
          </div>
          <Stat label="source bytes/s" value={report.bps ? formatBytes(report.bps) : '–'} />
          <Stat label="bytes seen" value={formatBytes(report.life_bytes)} />
          <Stat label="parameters" value={formatCount(report.parameters || (run.summary || {}).parameters)} />
          <Stat label="spent" value={`$${run.spent.toFixed(2)} of $${run.spend_cap}`} tone={run.spent >= run.spend_cap * 0.9 ? SIGNAL : undefined} />
          <Stat label="machine" value={`$${(run.dollars_per_hour || 0).toFixed(3)}/h · ${run.hours.toFixed(1)}h rented`} />
        </div>

        {progress !== null && (
          <div>
            <div className="h-1 bg-[#1e251f] rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${Math.min(100, progress * 100)}%`, background: LIVE }} />
            </div>
            <div className="flex justify-between gap-3 mt-1.5 font-mono text-[10px] text-[#666666] lowercase">
              <span className="truncate">{latest.stage || ''}</span>
              <span className="shrink-0">{(progress * 100).toFixed(1)}% of the plan</span>
            </div>
          </div>
        )}

        {run.kind === 'benchmark' ? (
          <Panel title="throughput" note={benchmark.length ? `${machine.gpu || run.gpu_name}` : 'measuring'}>
            <div className="font-mono text-sm space-y-1">
              {benchmark.map(b => (
                <div key={b.width} className="grid grid-cols-4 gap-3 text-[#aaaaaa]">
                  <span className="text-white">width {b.width}</span>
                  <span>{b.bps ? `${formatBytes(b.bps)}/s` : <span style={{ color: DANGER }}>{(b.error || 'failed').slice(0, 60)}</span>}</span>
                  <span>{formatCount(b.parameters)} params</span>
                  <span>{b.gpu_memory_peak_mb ? `${(b.gpu_memory_peak_mb / 1024).toFixed(1)} GB` : ''}{b.controllers ? ` · controllers ${b.controllers}` : ''}{b.stride ? ` · stride ${b.stride}` : ''}</span>
                  {b.updates_per_second ? (
                    <span className="col-span-4 text-[#666666] text-xs">
                      {b.updates_per_second.toFixed(2)} updates/s · {b.bits_per_prediction?.toFixed(2)} bits per prediction · {b.learned_bits_per_second ? `${formatCount(b.learned_bits_per_second)} bits learned/s` : ''}
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          </Panel>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Panel title="bits per byte" note={bpbSeries.length > 1 ? `${bpbSeries.length} points · raw, fast and slow averages${validation.length ? ` · held-out ${validation[validation.length - 1].validation_bpb.toFixed(3)}` : ''}` : 'waiting for points'}>
                <LossChart values={bpbSeries} />
              </Panel>
              <Panel title="residual by band" note={typeof band === 'number' ? `traversal band ${band.toFixed(1)} · every ${Math.max(1, Math.round(1.618 ** band))} tokens` : null}>
                <ResidualBars values={report[residualKey] || []} baseline={report.residual_baseline || []}
                  excess={residualKey === 'residual_spectrum' ? (report.residual_excess || []) : []}
                  centre={typeof band === 'number' ? band : null} />
              </Panel>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Panel title="reading" note={scored.length ? `mean ${(scored.reduce((a, b) => a + b, 0) / scored.length).toFixed(1)} bits · brighter cost more` : 'waiting for a report'}>
                <SurpriseText tokens={report.surprise || []} />
              </Panel>
              <Panel title="listening" note={centre !== null ? `centre band ${centre.toFixed(1)}${startCentre !== null ? ` · run start ${startCentre.toFixed(1)}` : ''}` : 'waiting for a report'}>
                <ListeningBars now={report.listening || []} start={latest.listening_start} />
              </Panel>
            </div>
            {dream && (
              <Panel title="dreaming" note={dream.bytes_seen ? `at ${formatBytes(dream.bytes_seen)}` : null}>
                <p className="font-mono text-xs text-[#aaaaaa] whitespace-pre-wrap break-words leading-5">{dream.text}</p>
              </Panel>
            )}
          </>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <Panel title="machine" note={run.heartbeat_at ? `heard from ${ago(run.heartbeat_at)}` : 'not heard from yet'}>
            <div className="font-mono text-xs text-[#aaaaaa] space-y-1">
              <div>{machine.gpu || run.gpu_name}{machine.gpu_memory_mb ? ` · ${(machine.gpu_memory_mb / 1024).toFixed(0)} GB` : ''}</div>
              {latest.gpu_memory_used_mb ? <div>{(latest.gpu_memory_used_mb / 1024).toFixed(1)} GB in use</div> : null}
              <div>{run.provider} instance {run.instance_id || '–'}{run.interruptible ? ' · interruptible' : ''}</div>
              {run.resumes > 0 && <div style={{ color: SIGNAL }}>moved machines {run.resumes}×</div>}
              {!run.house && <div>${(run.price_per_hour || 0).toFixed(3)}/h billed</div>}
              <div>{latest.train_seconds ? `${(latest.train_seconds / 3600).toFixed(2)}h training of ${run.hours.toFixed(2)}h rented` : `${run.hours.toFixed(2)}h rented`}</div>
              <div>limit {run.max_hours}h · started {run.rented_at ? ago(run.rented_at) : '–'}</div>
              {run.destroyed_at && <div className="text-[#666666]">machine destroyed {ago(run.destroyed_at)}</div>}
            </div>
          </Panel>
          <div className="md:col-span-2">
            <Panel title="trail" note={`${trail.length} events`}>
              <div className="font-mono text-[11px] leading-5 max-h-64 overflow-y-auto border border-[#1e251f] rounded-lg p-3 flex flex-col-reverse">
                <div>
                  {trail.map(e => {
                    const [time, text, colour] = trailLine(e);
                    return <div key={e.seq} className="break-words"><span className="text-[#444444]">{time} </span><span style={{ color: colour }}>{text}</span></div>;
                  })}
                </div>
              </div>
            </Panel>
          </div>
        </div>
      </div>
    </div>
  );
}
